import classNames from "classnames";
import {
	BusyIndicator,
	DropOverContext,
	DropOverMenu,
	NoResults
} from "client/Components";
import { DisplayContactModel, getContactsStartsWith } from "client/Api/Email/contacts";
import {
	AlertManager,
	useAlerts,
	useDebouncedValue,
	useKeyboardListNavigation,
	KeyBoardListNavigationContext,
	useKeyboardListNavigationItem
} from "client/Hooks";
import {
	generateTrackingId,
	isNil,
	isNotNil,
	isNotNilOrEmpty,
	setTrackingId
} from "client/Misc/utils";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import {
	ChangeEvent,
	FormEvent,
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useCallback,
	useContext,
	useEffect,
	useImperativeHandle,
	useRef,
	useState
} from "react";
import textInputStyles from "../../../../../Components/TextInput/TextInput.module.less";
import styles from "./RecipientsAutocomplete.module.less";
import { RecipientListItem } from "./RecipientListItem";

export type RecipientsAutocompleteHandle = {
	focusInput(): void;
};

export type RecipientsAutocompleteProps = {
	value: string;
	onChange(newValue: string): void;
	isInvalid?: boolean;
	trackingId: string;
	menuTitle: string;
};

export const RecipientsAutocomplete = forwardRef(
	(props: RecipientsAutocompleteProps, ref: Ref<RecipientsAutocompleteHandle>): ReactElement => {
		const {
			value,
			onChange,
			isInvalid,
			trackingId,
			menuTitle
		} = props;

		const inputRef: RefObject<HTMLInputElement | null> = useRef<HTMLInputElement>(null);

		const [autoCompleteState, setAutoCompleteState] = useState<AutoCompleteState>({
			isOpen: false
		});

		const changeHandler = useCallback(
			(args: ChangeEvent<HTMLInputElement>): void => {
				const newValue: string = args.target
					.value;

				onChange(newValue);
			},
			[onChange]
		);

		const triggerAutoComplete = useCallback(
			(value: string, position: number): void => {
				const entries: Entry[] = splitRecipients(value);
				const indexOfEntryAtPosition: number = entries.filter(e => e.position < position)
					.length - 1;

				if (indexOfEntryAtPosition === -1) {
					return;
				}

				if (isNotNilOrEmpty(entries[indexOfEntryAtPosition].label)) {
					setAutoCompleteState({
						isOpen: true,
						entries: entries,
						lookUpEntryIndex: indexOfEntryAtPosition
					});
				} else {
					setAutoCompleteState({
						isOpen: false
					});
				}
			},
			[]
		);

		const inputHandler = useCallback(
			(args: FormEvent<HTMLInputElement>): void => {
				const {
					value,
					selectionStart: position
				} = args.currentTarget;

				if (isNil(position)) {
					return;
				}

				triggerAutoComplete(value, position);
			},
			[triggerAutoComplete]
		);

		const selectAutoCompleteValueHandler = useCallback(
			(value: string): void => {
				if (!autoCompleteState.isOpen) {
					return;
				}

				onChange(
					autoCompleteState.entries
						.map((e, i) =>
							i === autoCompleteState.lookUpEntryIndex
								? value
								: e.label
						)
						.join(`${EMAIL_SEPARATOR} `) + `${EMAIL_SEPARATOR} `
				);
				inputRef.current
					?.focus();
			},
			[autoCompleteState, onChange]
		);

		const closeHandler = useCallback(
			(): void => {
				setAutoCompleteState({isOpen: false});
			},
			[]
		);

		const focusInputHandler = useCallback(
			(): void => {
				inputRef.current
					?.focus();
			},
			[]
		);

		const {
			contextData,
			keyDownHandler
		} = useKeyboardListNavigation({
			onFocusInput: focusInputHandler,
			onCancel: closeHandler
		});

		useImperativeHandle(
			ref,
			(): RecipientsAutocompleteHandle => ({
				focusInput(): void {
					inputRef.current
						?.focus();
				},
			}),
			[]
		);

		return (
			<KeyBoardListNavigationContext.Provider value={contextData}>
				<input
					{...setTrackingId(trackingId)}
					className={classNames(
						textInputStyles.root,
						{[textInputStyles.root_isInvalid]: isInvalid}
					)}
					type="text"
					autoComplete="off"
					value={value}
					onChange={changeHandler}
					ref={inputRef}
					onInput={inputHandler}
					onKeyDown={keyDownHandler}
				/>
				{autoCompleteState.isOpen && inputRef.current && (
					<AutoCompleteDropOver
						searchTerm={
							autoCompleteState
								.entries[autoCompleteState.lookUpEntryIndex]
								.label
						}
						onSelect={selectAutoCompleteValueHandler}
						target={inputRef.current}
						onClose={closeHandler}
						trackingId={generateTrackingId(trackingId, "__drop-over")}
						menuTitle={menuTitle}
					/>
				)}
			</KeyBoardListNavigationContext.Provider>
		);
	}
);
RecipientsAutocomplete.displayName = "RecipientsAutocomplete";

type AutoCompleteDropOverProps = {
	target: HTMLElement;
	searchTerm: string;
	onSelect(value: string): void;
	onClose(): void;
	trackingId: string;
	menuTitle: string;
};

const AutoCompleteDropOver = (props: AutoCompleteDropOverProps): ReactElement => {
	const {
		target,
		onSelect,
		searchTerm,
		onClose,
		trackingId,
		menuTitle
	} = props;

	const alertManager: AlertManager = useAlerts();
	const [items, setItems] = useState<readonly DisplayContactModel[]>([]);
	const [isBusy, setIsBusy] = useState<boolean>(false);
	const debouncedSearchTerm: string | undefined = useDebouncedValue(searchTerm, 250);

	const shouldShowBusyIndicator: boolean = isBusy || !debouncedSearchTerm;

	useEffect(
		() => {
			async function fetchData(
				searchTerm: string,
				signal: AbortSignal
			): Promise<void> {
				setIsBusy(true);

				try {
					const items: readonly DisplayContactModel[] | undefined = await getContactsStartsWith(
						searchTerm,
						null,
						signal
					);

					if (isNotNil(items)) {
						setItems(items);
					}

					setIsBusy(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setIsBusy(false);
					console.error(e);
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			}

			if (!debouncedSearchTerm) {
				return undefined;
			}

			const cancellation = new AbortController();
			fetchData(debouncedSearchTerm, cancellation.signal);

			return () => cancellation.abort();
		},
		[alertManager, debouncedSearchTerm]
	);

	return (
		<DropOverMenu
			target={target}
			hasNoPadding
			onClose={onClose}
			hasResponsiveDropOver={false}
			title={menuTitle}
			trackingId={generateTrackingId(trackingId, "__wrapper")}
		>
			<div
				{...setTrackingId(trackingId)}
				className={styles.menu}
			>
				{shouldShowBusyIndicator && <BusyIndicator />}
				{items.length > 0 && (
					<ul
						{...setTrackingId("list")}
						className={styles.list}
					>
						{items.map(
							(item, index) => (
								<AutoCompleteItem
									key={index}
									index={index}
									item={item}
									onSelect={() => onSelect(item.emailAddress)}
								/>
							)
						)}
					</ul>
				)}
				{items.length === 0 && !shouldShowBusyIndicator && (
					<NoResults
						icon="user"
						title={localize("_NoSuggestions")}
						message={localize("_NoSuggestedInfo")}
					/>
				)}
			</div>
		</DropOverMenu>
	);
};
AutoCompleteDropOver.displayName = "AutoCompleteDropOver";

type AutoCompleteItemProps = {
	index: number;
	item: DisplayContactModel;
	onSelect(): void;
};
const AutoCompleteItem = (props: AutoCompleteItemProps): ReactElement => {
	const {
		index,
		item,
		onSelect
	} = props;

	const {
		onClose
	} = useContext(DropOverContext);

	const selectHandler = (): void => {
		onSelect();
		onClose?.();
	};

	const {
		onKeyDown,
		ref
	} = useKeyboardListNavigationItem({
		itemIndex: index,
		onExecute: selectHandler
	});

	return (
		<li
			ref={ref}
			onKeyDown={onKeyDown}
			tabIndex={-1}
			className={styles.item}
		>
			<RecipientListItem
				item={item}
				onClick={selectHandler}
				trackingId="recipient"
			/>
		</li>
	);
};
AutoCompleteItem.displayName = "AutoCompleteItem";

type Entry = {
	label: string;
	position: number;
};

type AutoCompleteState = {
	isOpen: false;
} | {
	isOpen: true;
	entries: readonly Entry[];
	lookUpEntryIndex: number;
};

export const EMAIL_SEPARATOR: string = ";";

function splitRecipients(value: string): Entry[] {
	let position: number = 0;
	return value.split(EMAIL_SEPARATOR)
		.map((e: string): Entry => {
			const result: Entry = {
				label: e.trim(),
				position
			};

			position += e.length + 1;

			return result;
		})
		.filter(e => isNotNilOrEmpty(e.label));
}